import React, { useState, useMemo } from "react";
import { AvatarChip } from "./AvatarChip";
import { createChallenge } from "../lib/api";
import { SmartScheduleSuggestions } from "./SmartScheduleSuggestions";

export function ChallengeModal({ fromTeam, toTeam, myManagedTeams, onPickFromTeam, onClose, onSent, onToast }) {
  // Default to "tomorrow at 8pm" (local) so the picker is never empty.
  const defaultWhen = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(20, 0, 0, 0);
    // Format as YYYY-MM-DDTHH:mm for <input type=datetime-local>.
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);
  const [scheduledLocal, setScheduledLocal] = useState(defaultWhen);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (!toTeam) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (!fromTeam) {
      onToast?.("Pick which of your teams is challenging.");
      return;
    }
    const dt = new Date(scheduledLocal);
    if (Number.isNaN(dt.getTime()) || dt < new Date()) {
      onToast?.("Pick a future date and time.");
      return;
    }
    setBusy(true);
    try {
      await createChallenge({
        from_team_id: fromTeam.team_id,
        to_team_id: toTeam.team_id,
        scheduled_at: dt.toISOString(),
        note: note.trim(),
      });
      onSent?.();
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Could not send challenge.");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[98] bg-black/85 backdrop-blur-md p-4 flex items-center justify-center"
      onClick={onClose}
      data-testid="challenge-backdrop"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="glass rounded-3xl w-full max-w-lg neon-glow p-6 space-y-4 border-2 border-[#14b8a6]/40"
        data-testid="challenge-modal"
      >
        <div className="flex items-center justify-between">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
            <i className="fas fa-bolt mr-1" />SEND CHALLENGE
          </p>
          <button
            type="button"
            onClick={onClose}
            data-testid="challenge-close-btn"
            className="text-white/60 hover:text-white text-2xl"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Matchup preview */}
        <div className="grid grid-cols-3 items-center gap-2 py-2">
          <div className="text-center">
            {fromTeam ? (
              <>
                <AvatarChip avatarId={fromTeam.avatar_id} pfpUrl={fromTeam.pfp_url} size={56} />
                <p className="text-xs font-bold mt-1 truncate">{fromTeam.name}</p>
                <p className="text-[10px] text-[#a3a3ac]">MMR {fromTeam.mmr || 0}</p>
              </>
            ) : (
              <p className="text-[10px] text-white/40">pick your team ↓</p>
            )}
          </div>
          <div className="text-center font-display text-2xl grad-text">VS</div>
          <div className="text-center">
            <AvatarChip avatarId={toTeam.avatar_id} pfpUrl={toTeam.pfp_url} size={56} />
            <p className="text-xs font-bold mt-1 truncate">{toTeam.name}</p>
            <p className="text-[10px] text-[#a3a3ac]">MMR {toTeam.mmr || 0}</p>
          </div>
        </div>

        {/* Pick which managed team if multiple */}
        {(myManagedTeams || []).length > 1 && (
          <div data-testid="challenge-from-picker" className="border-t border-white/10 pt-3">
            <p className="text-[10px] tracking-widest text-[#a3a3ac] mb-1">YOUR TEAM</p>
            <div className="flex flex-wrap gap-2">
              {myManagedTeams.map((t) => (
                <button
                  key={t.team_id}
                  type="button"
                  onClick={() => onPickFromTeam?.(t)}
                  data-testid={`challenge-pick-from-${t.team_id}`}
                  className={`text-xs px-3 py-1.5 rounded-lg border ${
                    fromTeam?.team_id === t.team_id
                      ? "bg-[#14b8a6]/20 border-[#14b8a6] text-[#14b8a6]"
                      : "bg-black/40 border-white/15 text-white/70 hover:border-[#14b8a6]/50"
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <label className="block">
          <span className="text-[10px] tracking-widest text-[#a3a3ac]">
            <i className="fas fa-clock mr-1" />SCHEDULED TIME (your local TZ)
          </span>
          <input
            type="datetime-local"
            value={scheduledLocal}
            onChange={(e) => setScheduledLocal(e.target.value)}
            data-testid="challenge-scheduled-at"
            className="mt-1 w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white"
            required
          />
        </label>

        {/* Wave C — Smart Schedule suggestions based on availability overlap */}
        {fromTeam && (
          <SmartScheduleSuggestions
            fromTeamId={fromTeam.team_id}
            toTeamId={toTeam.team_id}
            onToast={onToast}
            onPick={(iso) => {
              try {
                const d = new Date(iso);
                const pad = (n) => String(n).padStart(2, "0");
                setScheduledLocal(
                  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`,
                );
                onToast?.("Time picked from suggestions — review and send.");
              } catch { /* ignore */ }
            }}
          />
        )}

        <label className="block">
          <span className="text-[10px] tracking-widest text-[#a3a3ac]">NOTE (optional)</span>
          <textarea
            rows={2}
            maxLength={280}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Best of 3 maps. Discord comms?"
            data-testid="challenge-note"
            className="mt-1 w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white"
          />
        </label>

        <div className="flex gap-2 pt-2 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            data-testid="challenge-cancel"
            className="flex-1 btn-ghost rounded-2xl py-3"
          >
            CANCEL
          </button>
          <button
            type="submit"
            disabled={busy || !fromTeam}
            data-testid="challenge-send"
            className="flex-1 rounded-2xl py-3 bg-[#14b8a6] text-black font-bold tracking-widest disabled:opacity-40 hover:brightness-110"
          >
            {busy ? "SENDING..." : "⚡ SEND CHALLENGE"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ChallengeModal;
